import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IAdvertPaginatedItem, IProvinceProp } from "./props";
import { apiUrl } from "../../config";
import apiService from "../../shared/apiService";
import { IBaseModel, IPaginate } from "../types";
import { showErrorToast } from "../../components/CustomNotification";
import { AxiosError } from "axios";
import { format, isAfter } from "date-fns";
import apiRequest from "../../Utils/apiRequest";
import { useDebounceEffect } from "../../Utils/useDebounceEffect";

export const useAdverts = () => {
  const { t, i18n } = useTranslation();

  const [provinces, setProvinces] = useState<IProvinceProp[]>();
  const [selectedProvinces, setSelectedProvinces] = useState<number>();

  const [loading, setLoading] = useState<boolean>();
  const [notFound, setNotFound] = useState<boolean>();
  const [data, setData] = useState<IPaginate<IAdvertPaginatedItem>>();
  const [currentPage, setCurrentPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [filter, setFilter] = useState<boolean>();

  const getProvinces = async () => {
    setLoading(true);
    try {
      var response = (await apiService.get(
        `${apiUrl}Province/GetList`
      )) as IBaseModel<IProvinceProp[]>;

      if (!response.success) {
        showErrorToast(response?.message ?? t("simple_error"));
      }
      setProvinces(response.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        showErrorToast(
          (error.response?.data as IBaseModel<any>)?.message ??
            t("simple_error")
        );
      } else {
        showErrorToast(t("simple_error"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProvinces();
  }, []);

  const getData = useCallback(
    async (
      currentPage: number,
      startDate: Date | undefined,
      endDate: Date | undefined,
      searchText: string,
      selectedProvince: number | undefined
    ) => {
      setNotFound(false);
      setLoading(true);
      try {
        var filter = {
          field: "isActive",
          logic: "and",
          operator: "eq",
          value: "true",
        };
        const filters = [
          {
            field: "IsDeleted",
            operator: "neq",
            logic: "and",
            value: "true",
          },
        ];

        if (
          startDate &&
          startDate instanceof Date &&
          isAfter(startDate, new Date(29, 10, 1923))
        ) {
          filters.push({
            field: "Createdate",
            operator: "gte",
            logic: "and",
            value: format(startDate, "yyyy-MM-dd 00:00"),
          });
        }

        if (
          endDate &&
          endDate instanceof Date &&
          isAfter(endDate, new Date(29, 10, 1923))
        ) {
          filters.push({
            field: "Createdate",
            operator: "lte",
            logic: "and",
            value: format(endDate, "yyyy-MM-dd 23:59"),
          });
        }

        if (searchText && searchText !== "") {
          filters.push({
            field: "Position.JobDescription.JobDescriptionsName",
            operator: "contains",
            logic: "and",
            value: searchText,
          });
        }

        if (selectedProvince) {
          filters.push({
            field: "Position.ProvinceId",
            operator: "eq",
            logic: "and",
            value: selectedProvince.toString(),
          });
        }

        const response = (await apiRequest(
          "POST",
          `${apiUrl}Advert/getExternalDynamicList`,
          {
            page: currentPage,
            pageSize: 10,
            dynamic: {
              sort: [
                {
                  field: "createdate",
                  dir: "desc",
                },
              ],
              filter: {
                ...filter,
                filters: filters,
              },
            },
          }
        )) as IBaseModel<IPaginate<IAdvertPaginatedItem>>;

        if (!response.success) {
          showErrorToast(
            response.message && response.message !== ""
              ? response.message
              : t("simple_error")
          );
          return;
        }

        const result = response.data;

        if (!result) {
          showErrorToast(
            response.message && response.message !== ""
              ? response.message
              : t("simple_error")
          );
          return;
        }
        if ((result.items?.length ?? 0) <= 0) {
          // showErrorToast(
          //   response.message && response.message !== ""
          //     ? response.message
          //     : t("not_found_property", {
          //         property: t("advert"),
          //       })
          // );
          setNotFound(true);
          return;
        }
        setData(result);
      } catch (error: any) {
        if (error instanceof AxiosError) {
          showErrorToast(
            (error.response?.data as IBaseModel<any>)?.message ??
              t("simple_error")
          );
        } else {
          showErrorToast(t("simple_error"));
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      getData(currentPage, startDate, endDate, searchText, selectedProvinces);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, []);

useDebounceEffect(
  () => {
    // Sadece 3 veya daha fazla karakter varsa istek at
    if (searchText.length >= 3 || searchText.length === 0) {
      getData(currentPage, startDate, endDate, searchText, selectedProvinces);
    }
  },
  1000,
  [currentPage, startDate, endDate, searchText, selectedProvinces]
);


  return {
    selectedProvinces,
    provinces,
    startDate,
    notFound,
    endDate,
    loading,
    filter,
    data,
    t,

    setSelectedProvinces,
    setCurrentPage,
    setSearchText,
    setStartDate,
    setEndDate,
    setFilter,
  };
};
